// BUILD-042 (Oct 17) - "What needs attention": the pure screen logic (no DOM, no React, no network), tested under plain Node
// (attentionModel.test.mjs). The SERVER decides what counts and who may see it (lib/lambda/hr/hr-attention.ts); this module only turns
// what the server returned (or failed to return) into what a badge, banner or list may truthfully say.
//
// The one rule that matters most here: a failed or unfinished check is NEVER shown as zero and never as "nothing needs attention".
// Only a successful response with a total of 0 is "nothing to do", and even then the badge/warning is simply not drawn.

/** The words shown to an employee for a document that is with HR. Exact wording is a requirement (Oct 17). */
export const AWAITING_HR_REVIEW_EMPLOYEE_LABEL = 'Awaiting HR review — no action needed from you';

/** Mirrors the server: only these document statuses are the EMPLOYEE's action. Awaiting HR review is not. */
export function isEmployeeActionStatus(status: string): boolean {
  return status === 'awaiting_return' || status === 'needs_correction';
}

export type AttentionSection = 'documents' | 'checklists' | 'corrections';

export interface MyAttentionItem {
  itemId: string;
  kind: 'signature' | 'document' | 'correction' | 'task';
  title: string;
  detail: string;
  dueDate: string | null;
  section: AttentionSection;
  targetId: string;
}
export interface MyAttention { total: number; items: MyAttentionItem[] }

export type AdminAttentionState = 'awaiting_hr_review' | 'awaiting_return' | 'needs_correction' | 'not_started' | 'in_progress';
export interface AdminAttentionItem {
  itemId: string;
  /** review = waiting for HR review; awaiting_employee = a document still with the employee; task = an open checklist task;
   *  correction = an open employee profile correction request waiting for HR review. */
  kind: 'review' | 'awaiting_employee' | 'task' | 'correction';
  employeeId: string;
  employeeName: string | null;
  title: string;
  detail: string;
  section: AttentionSection;
  targetId: string;
  state: AdminAttentionState;
  /** Tasks: the real responsibility label. A routing label, not a permission. */
  responsibleRole: string | null;
  /** Tasks: the stored due date. Documents have none. */
  dueDate: string | null;
  /** Cycle 13, tasks only: the checklist kind (onboarding / offboarding). Absent on an older server. */
  processKind?: 'onboarding' | 'offboarding' | null;
}
/** Cycle 13: open checklist tasks split by kind. */
export interface TaskKindCounts { onboarding: number; offboarding: number }
export interface AdminCounts { awaitingEmployee: number; needsHrReview: number; tasks: number; corrections: number; total: number }
export interface AdminAttention {
  authorized: boolean;
  total: number;
  items: AdminAttentionItem[];
  byEmployee: Record<string, number>;
  byEmployeeDetail: Record<string, AdminCounts>;
  counts: AdminCounts;
  truncated: boolean;
  /** Cycle 13 (additive): the open-task population split by checklist kind. Present only when the caller may see tasks; absent on an older server or without task authority. */
  byEmployeeTaskKind?: Record<string, TaskKindCounts>;
  taskKindCounts?: TaskKindCounts;
}
export type PendingStateFilter = 'all' | 'awaiting_employee' | 'needs_hr_review' | 'tasks' | 'corrections';
export interface PendingWorkPage {
  authorized: boolean;
  total: number;
  offset: number;
  limit: number;
  items: AdminAttentionItem[];
  counts: AdminCounts;
  byEmployee: Record<string, number>;
  employees: Array<{ employeeId: string; employeeName: string | null; count: number }>;
}

/** What an employee is asked to do about ONE assigned document, in the words shown on the row. Null = nothing for the employee to do. Mirrors the server. */
export interface EmployeeActionDocument {
  status: string;
  cancelledAt?: string | null;
  movedToHistoryAt?: string | null;
  documentSigning?: { signing: { status?: string; employeeSigned?: boolean } | null } | null;
  equipmentCheckout?: { signing: { status?: string; employeeSigned?: boolean } | null } | null;
}
export type EmployeeActionLabel = 'Signature needed' | 'Upload needed' | 'Correction needed';
export function employeeDocumentAction(d: EmployeeActionDocument): { label: EmployeeActionLabel; kind: 'signature' | 'document' | 'correction' } | null {
  if (d.cancelledAt || d.movedToHistoryAt) return null;
  if (!isEmployeeActionStatus(d.status)) return null;
  const signing = d.documentSigning?.signing ?? d.equipmentCheckout?.signing ?? null;
  // Signed by the employee and still with the provider/HR: nothing left for the employee to do (same rule as the server).
  if (signing && signing.employeeSigned && signing.status !== 'cancelled' && signing.status !== 'failed') return null;
  if (d.status === 'needs_correction') return { label: 'Correction needed', kind: 'correction' };
  const esign = !!d.documentSigning || !!d.equipmentCheckout;
  return esign ? { label: 'Signature needed', kind: 'signature' } : { label: 'Upload needed', kind: 'document' };
}

/** One fetched thing, with an honest status. `data` is the last good response (kept while refreshing). */
export interface AttentionSlice<T> {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: T | null;
  error: string | null;
}
export function idleSlice<T>(): AttentionSlice<T> {
  return { status: 'idle', data: null, error: null };
}

export type BadgeTone = 'none' | 'count' | 'error';
export interface BadgeView {
  /** Draw anything at all? False for: not yet known, not authorized, and a genuine zero. */
  show: boolean;
  tone: BadgeTone;
  /** What is printed inside the badge ('' when nothing is printed). */
  text: string;
  count: number;
  /** Spoken label for assistive technology. */
  label: string;
}

const HIDDEN: BadgeView = { show: false, tone: 'none', text: '', count: 0, label: '' };

export function formatCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

/** Turn a slice into what a numbered badge may show. `noun` is the thing being counted, e.g. "item". */
export function badgeFor<T extends { total: number }>(slice: AttentionSlice<T>, scope: string): BadgeView {
  if (slice.status === 'error') {
    // A failure is never zero. The last good number is NOT reused either, since it may be wrong now.
    return { show: true, tone: 'error', text: '!', count: 0, label: `${scope}: could not check what needs attention` };
  }
  if (!slice.data) return HIDDEN; // idle / first load in flight: say nothing rather than guess
  const authorized = (slice.data as unknown as { authorized?: boolean }).authorized;
  if (authorized === false) return HIDDEN;
  const n = slice.data.total;
  if (!Number.isFinite(n) || n <= 0) return HIDDEN; // a genuine, successful zero: nothing to draw
  return { show: true, tone: 'count', text: formatCount(n), count: n, label: `${scope}: ${n} ${n === 1 ? 'item needs' : 'items need'} attention` };
}

/** The reviewer's own badge: ONLY the correction-request count (a reviewer who is not an HR administrator is never shown a document/task total).
 *  Error -> "!", unknown / not authorized / zero -> nothing; same rules as every other attention badge. */
export function correctionBadgeFor(slice: AttentionSlice<AdminAttention>, scope: string): BadgeView {
  if (slice.status === 'error') return { show: true, tone: 'error', text: '!', count: 0, label: `${scope}: could not check what needs attention` };
  const data = slice.data;
  if (!data || data.authorized === false) return HIDDEN;
  const n = data.counts?.corrections ?? 0;
  return sectionBadge(n, true, false, scope);
}

/** A section-menu badge from already-derived section counts. Error -> "!", unknown or zero -> nothing. */
export function sectionBadge(count: number, known: boolean, errored: boolean, scope: string): BadgeView {
  if (errored) return { show: true, tone: 'error', text: '!', count: 0, label: `${scope}: could not check what needs attention` };
  if (!known || !Number.isFinite(count) || count <= 0) return HIDDEN;
  return { show: true, tone: 'count', text: formatCount(count), count, label: `${scope}: ${count} ${count === 1 ? 'item needs' : 'items need'} attention` };
}

/** The badge for a menu GROUP made of its items' badges: any failed item -> "!", otherwise their counts added (same person, same domain). */
export function groupBadgeFor(groupLabel: string, items: Array<BadgeView | undefined>): BadgeView {
  const shown = items.filter((v): v is BadgeView => !!v && v.show);
  if (shown.length === 0) return HIDDEN;
  if (shown.some((v) => v.tone === 'error')) return { show: true, tone: 'error', text: '!', count: 0, label: `${groupLabel}: could not check what needs attention` };
  const n = shown.reduce((a, v) => a + v.count, 0);
  return { show: true, tone: 'count', text: formatCount(n), count: n, label: `${groupLabel}: ${n} ${n === 1 ? 'item needs' : 'items need'} attention` };
}

/** Per-employee count for the roster. Only a successful, authorized response may produce a number; everything else produces none. */
export function employeeBadge(slice: AttentionSlice<AdminAttention>, employeeId: string): BadgeView {
  if (slice.status === 'error' || !slice.data || slice.data.authorized === false) return HIDDEN;
  const n = slice.data.byEmployee[employeeId] ?? 0;
  if (n <= 0) return HIDDEN;
  return { show: true, tone: 'count', text: formatCount(n), count: n, label: `${n} ${n === 1 ? 'item needs' : 'items need'} HR attention` };
}

/** Counts of one employee's items per workspace section (for the section menu and the overview). Never invents a zero on error. */
export function employeeSectionCounts(slice: AttentionSlice<AdminAttention>, employeeId: string): {
  documents: number; checklists: number; total: number; known: boolean;
  /** Exact split of this employee's open work (from the server, independent of the list cap). */
  awaitingEmployee: number; needsHrReview: number; tasks: number;
} {
  if (slice.status === 'error' || !slice.data || slice.data.authorized === false) return { documents: 0, checklists: 0, total: 0, known: false, awaitingEmployee: 0, needsHrReview: 0, tasks: 0 };
  const mine = slice.data.items.filter((i) => i.employeeId === employeeId);
  const documents = mine.filter((i) => i.section === 'documents').length;
  const checklists = mine.filter((i) => i.section === 'checklists').length;
  const total = slice.data.byEmployee[employeeId] ?? 0;
  const d = slice.data.byEmployeeDetail?.[employeeId];
  // The list can be shortened (truncated) while byEmployee stays exact: never report fewer than the exact total.
  return {
    documents: d ? d.awaitingEmployee + d.needsHrReview : documents,
    checklists: d ? d.tasks : checklists,
    total: Math.max(total, documents + checklists),
    known: true,
    awaitingEmployee: d?.awaitingEmployee ?? mine.filter((i) => i.kind === 'awaiting_employee').length,
    needsHrReview: d?.needsHrReview ?? mine.filter((i) => i.kind === 'review').length,
    tasks: d?.tasks ?? mine.filter((i) => i.kind === 'task').length,
  };
}

/**
 * Cycle 13: one employee's open checklist tasks split by kind, for the Onboarding and Offboarding rail badges. SAME population as `employeeSectionCounts().tasks`
 * (not started / in progress, linked-document de-duplicated) - the two kinds add up to it - but never the combined number shown for both.
 * `known` is false when the check failed, is not authorized, or the server did not report a per-kind split (an older server, or a caller without task authority):
 * the badge then shows nothing (or "!" for a failed check) rather than guessing or reusing the combined count.
 */
export function employeeChecklistKindCounts(slice: AttentionSlice<AdminAttention>, employeeId: string): { known: boolean; onboarding: number; offboarding: number } {
  const unknown = { known: false, onboarding: 0, offboarding: 0 };
  if (slice.status === 'error' || !slice.data || slice.data.authorized === false) return unknown;
  const split = slice.data.byEmployeeTaskKind;
  if (split) { const e = split[employeeId]; return { known: true, onboarding: e?.onboarding ?? 0, offboarding: e?.offboarding ?? 0 }; }
  // Older server with a complete (not shortened) list that carries the kind on every one of this person's tasks: count it from the list. Otherwise unknown.
  if (slice.data.truncated) return unknown;
  const tasks = slice.data.items.filter((i) => i.employeeId === employeeId && i.kind === 'task');
  if (tasks.some((t) => t.processKind !== 'onboarding' && t.processKind !== 'offboarding')) return unknown;
  return { known: true, onboarding: tasks.filter((t) => t.processKind === 'onboarding').length, offboarding: tasks.filter((t) => t.processKind === 'offboarding').length };
}

export function mySectionCounts(slice: AttentionSlice<MyAttention>): { documents: number; checklists: number; total: number; known: boolean } {
  if (slice.status === 'error' || !slice.data) return { documents: 0, checklists: 0, total: 0, known: false };
  const documents = slice.data.items.filter((i) => i.section === 'documents').length;
  const checklists = slice.data.items.filter((i) => i.section === 'checklists').length;
  return { documents, checklists, total: slice.data.total, known: true };
}

/** Where an attention item should take the user. Paths are the app's real routes; the query selects the section and the row to open. */
export function myItemPath(i: Pick<MyAttentionItem, 'section' | 'targetId'>): string {
  return `/hr?section=${i.section}&item=${encodeURIComponent(i.targetId)}`;
}
export function adminItemPath(i: Pick<AdminAttentionItem, 'section' | 'targetId' | 'employeeId'>): string {
  // A profile correction request is reviewed in the HR correction-requests queue, deep-linked to the exact request —
  // not inside an employee's document/checklist workspace section.
  // Oct 19 06:28: ONE path for everyone allowed to review. A reviewer who is not an HR administrator is served by the lite reviewer
  // workspace there; an HR administrator is redirected from it (HrPage) to the same panel inside HR Administration, request id preserved.
  if (i.section === 'corrections') return `/hr/corrections?request=${encodeURIComponent(i.targetId)}`;
  return `/hr/admin/employees/${encodeURIComponent(i.employeeId)}?section=${i.section}&item=${encodeURIComponent(i.targetId)}`;
}

/** Only these document/checklist sections can be opened INSIDE an employee workspace from an attention link; a
 *  corrections link goes to the review queue instead (see adminItemPath), so it is intentionally not returned here.
 *  The return type is deliberately narrowed to the two openable sections (not the full AttentionSection, which also
 *  includes 'corrections') so a caller can feed it straight into a workspace SectionKey. */
export function parseAttentionSection(raw: string | null): 'documents' | 'checklists' | null {
  return raw === 'documents' || raw === 'checklists' ? raw : null;
}

export const KIND_PHRASE: Record<MyAttentionItem['kind'] | AdminAttentionItem['kind'], string> = {
  signature: 'Sign', document: 'Return', correction: 'Correct', task: 'Do', review: 'Review', awaiting_employee: 'Open',
};

/** The action word on an HR-side item's link. KIND_PHRASE's 'correction' means "the EMPLOYEE should correct a document"
 *  ("Correct"); on the HR side a 'correction' item is a profile-correction REQUEST waiting for HR, so HR's action is to Review it. */
export function adminActionPhrase(kind: AdminAttentionItem['kind']): string {
  return kind === 'correction' ? 'Review' : KIND_PHRASE[kind];
}

/** The words HR sees for an item's state (roster/overview/pending list). */
export function adminStateLabel(i: Pick<AdminAttentionItem, 'kind' | 'state'>): string {
  if (i.kind === 'review') return 'Needs HR review';
  if (i.kind === 'correction') return 'Correction request';
  if (i.kind === 'awaiting_employee') return i.state === 'needs_correction' ? 'Sent back to employee' : 'Waiting on employee';
  return i.state === 'in_progress' ? 'Task in progress' : 'Task not started';
}

/** The one-line breakdown shown on an employee overview, e.g. "3 awaiting employee · 0 awaiting HR review". */
export function pendingBreakdownText(c: { awaitingEmployee: number; needsHrReview: number; tasks: number; corrections?: number }): string {
  const base = `${c.awaitingEmployee} awaiting employee · ${c.needsHrReview} awaiting HR review`;
  const parts = [base];
  if (c.tasks > 0) parts.push(`${c.tasks} ${c.tasks === 1 ? 'open task' : 'open tasks'}`);
  if ((c.corrections ?? 0) > 0) parts.push(`${c.corrections} correction ${c.corrections === 1 ? 'request' : 'requests'}`);
  return parts.join(' · ');
}

/** Roster row text for a count of open items. */
export function pendingBadgeLabel(n: number): string {
  return n === 1 ? '1 open item' : `${n} open items`;
}
